// src/components/NewRequest.js - New request form component
// src/components/NewRequest.js - New request form component
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { generateClient } from 'aws-amplify/api';
import { createProject } from '../graphql/mutations';
import { getCurrentUser } from 'aws-amplify/auth';

const client = generateClient();

function NewRequest() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectName: '',
    files: null,
    sourceLanguage: '',
    targetLanguages: '',
    dueDate: '',
    contentType: '',
    serviceType: '',
    urgencyLevel: '',
    specialInstructions: '',
    referenceFiles: null,
    poNumber: '',
    agreeToNDA: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'file' ? files : value)
    }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  try {
    console.log('=== DEBUGGING AUTH ===');
    console.log('User from context:', user);
    
    if (!user) {
      setError('Not logged in. Please log out and log back in.');
      setLoading(false);
      return;
    }

    // Create project data
    const projectData = {
      projectName: formData.projectName,
      name: formData.name,
      email: formData.email,
      sourceLanguage: formData.sourceLanguage,
      targetLanguages: formData.targetLanguages,
      dueDate: formData.dueDate,
      contentType: formData.contentType,
      serviceType: formData.serviceType,
      urgencyLevel: formData.urgencyLevel,
      specialInstructions: formData.specialInstructions || '',
      poNumber: formData.poNumber || '',
      status: 'Not Started'
    };

    console.log('Submitting project data:', projectData);

    const result = await client.graphql({
      query: createProject,
      variables: { input: projectData }
    });
    
    console.log('GraphQL result:', result);
    setSuccess(true);
    setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
  } catch (error) {
    console.error('Full error object:', error);
    setError('Failed to submit request. Please try again.');
  } finally {
    setLoading(false);
  }
};

  if (success) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6 font-sans text-gray-800 relative">
        <div className="flex justify-center mt-10">
          <img 
            src="https://compasslanguages.s3.us-east-2.amazonaws.com/logos/Bausch_Health_Logo.png" 
            alt="Bausch Health Logo" 
            className="h-20 object-contain" 
          />
        </div>
        <div className="text-center">
          <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-lg">
            <h2 className="text-2xl font-bold mb-2">Request Submitted Successfully!</h2>
            <p>Your translation request has been received. You will be redirected to the dashboard shortly.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6 font-sans text-gray-800 relative">
      {/* Top right buttons */}
      <div className="absolute right-6 top-6 flex gap-2">
        <button 
          onClick={() => navigate('/support')}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold px-4 py-2 rounded shadow"
        >
          Need support?
        </button>
      </div>
      
      {/* Bausch Health logo */}
      <div className="flex justify-center mt-10">
        <img 
          src="https://compasslanguages.s3.us-east-2.amazonaws.com/logos/Bausch_Health_Logo.png" 
          alt="Bausch Health Logo" 
          className="h-20 object-contain" 
        />
      </div>

      <h1 className="text-3xl font-bold text-primary text-center">New Translation Request</h1>

      <div className="shadow-xl border border-primary rounded-lg p-6 space-y-4 bg-white">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Form Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-semibold">
                Name <span className="text-red-500">*</span>
              </label>
              <input 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded focus:ring-2 focus:ring-primary" 
                placeholder=""
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input 
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded focus:ring-2 focus:ring-primary" 
                placeholder="you@bauschhealth.com"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input 
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded" 
                placeholder="Marketing Campaign Q3"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold">
                Upload File(s) <span className="text-red-500">*</span>
              </label>
              <input 
                name="files"
                type="file" 
                multiple
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold">
                Source Language <span className="text-red-500">*</span>
              </label>
              <select 
                name="sourceLanguage"
                value={formData.sourceLanguage}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded"
                required
              >
                <option value="">Select language</option>
                <option value="English">English</option>
                <option value="Spanish">Spanish</option>
                <option value="French">French</option>
                <option value="German">German</option>
                <option value="Japanese">Japanese</option>
                <option value="Chinese">Chinese</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-semibold">
                Target Language(s) <span className="text-red-500">*</span>
              </label>
              <select 
                name="targetLanguages"
                value={formData.targetLanguages}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded"
                required
              >
                <option value="">Select languages</option>
                <option value="German">German</option>
                <option value="Japanese">Japanese</option>
                <option value="Chinese">Chinese</option>
                <option value="Spanish">Spanish</option>
                <option value="French">French</option>
                <option value="Portuguese">Portuguese</option>
                <option value="Italian">Italian</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-semibold">
                Due Date <span className="text-red-500">*</span>
              </label>
              <input 
                name="dueDate"
                type="date" 
                value={formData.dueDate}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold">
                Content Type / Domain <span className="text-red-500">*</span>
              </label>
              <select 
                name="contentType"
                value={formData.contentType}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded"
                required
              >
                <option value="">Select domain</option>
                <option value="Legal">Legal</option>
                <option value="Technical">Technical</option>
                <option value="Marketing">Marketing</option>
                <option value="Medical">Medical</option>
                <option value="Regulatory">Regulatory</option>
                <option value="Training">Training</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-semibold">
                Service Type <span className="text-red-500">*</span>
              </label>
              <select 
                name="serviceType"
                value={formData.serviceType}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded"
                required
              >
                <option value="">Select service</option>
                <option value="Translation">Translation</option>
                <option value="Localization">Localization</option>
                <option value="Subtitling">Subtitling</option>
                <option value="Interpretation">Interpretation</option>
                <option value="Desktop Publishing">Desktop Publishing</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-semibold">
                Urgency Level <span className="text-red-500">*</span>
              </label>
              <select 
                name="urgencyLevel"
                value={formData.urgencyLevel}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded"
                required
              >
                <option value="">Select urgency</option>
                <option value="Normal">Normal</option>
                <option value="Rush">Rush</option>
                <option value="Same-day">Same-day</option>
              </select>
            </div>
          </div>

          {/* Special instructions */}
          <div>
            <label className="block text-gray-700 font-semibold">Special Instructions</label>
            <textarea 
              name="specialInstructions"
              value={formData.specialInstructions}
              onChange={handleInputChange}
              className="w-full border border-blue-300 p-2 rounded" 
              placeholder="Include glossary, follow brand tone, etc."
              rows="4"
            />
          </div>

          {/* Files and PO */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-semibold">Reference Files</label>
              <input 
                name="referenceFiles"
                type="file" 
                multiple
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded" 
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold">PO Number / Internal Code</label>
              <input 
                name="poNumber"
                value={formData.poNumber}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded" 
                placeholder="Optional internal reference" 
              />
            </div>
            <div className="col-span-2">
              <label className="text-gray-700 font-semibold flex items-center">
                <input 
                  name="agreeToNDA"
                  type="checkbox" 
                  checked={formData.agreeToNDA}
                  onChange={handleInputChange}
                  className="mr-2" 
                  required
                /> 
                Agree to confidentiality / NDA terms <span className="text-red-500 ml-1">*</span>
              </label>
            </div>
          </div>

          <div className="text-right space-x-2">
            <button 
              type="button"
              onClick={() => navigate('/dashboard')}
              className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-4 py-2 rounded"
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={loading}
              className="bg-primary hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>

      {/* Footer Section */}
      <div className="pt-8 text-center text-sm text-gray-600">
        Modular Customized Localization Platform by Compass Languages
        <div className="mt-2 flex justify-center">
          <img 
            src="https://compasslanguages.s3.us-east-2.amazonaws.com/logos/CompassLanguages_Logo4_PNG.png" 
            className="h-20" 
            alt="Compass Logo" 
          />
        </div>
      </div>
    </div>
  );
}

export default NewRequest;